var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import styled, { css } from 'styled-components';
import { readableColor } from 'polished';
import { fontBody, fontTitle, fontAccent } from 'Styles/fonts';
import * as colors from 'Styles/colors';
import breakpoint from 'Styles/breakpoints';
import { getBodySize, getEyebrowSize, getHeaderSize, getTextSectionPadding } from './utils';
import ButtonComponent from '../Button/Button';
import { ButtonStyle, parseButtonProps } from '../Button/Button.styled';
import { getTextSetConfig } from './components/TextSet/TextSet.utils';
import { applyTextWrap } from './utils/utils';
export var DEFAULT_HEADER_MAX_LINE_LENGTH = 64;
export var Icon = styled.img(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  max-width: 100%;\n  height: 48px;\n\n  ", " {\n    height: 64px;\n  }\n"], ["\n  max-width: 100%;\n  height: 48px;\n\n  ", " {\n    height: 64px;\n  }\n"])), breakpoint('large'));
var getEyebrowStyle = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 2 : _b;
    return "\n  ".concat(fontBody(getEyebrowSize({ size: size })), "\n  font-weight: bold;\n  margin-bottom: 2px;\n");
};
/**
 * IMPORTANT!
 *
 * For any of these text elements, they can have a dark color and a light color
 *
 * A dark color is included statically on the text element, and a light color is supplied dynamically to override the dark one.
 */
var getCTAStyle = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 2 : _b;
    return fontBody(getBodySize({ size: size }));
};
var generateCTAResponsiveStyles = function (_a) {
    var _b = _a.styles, styles = _b === void 0 ? [] : _b, _c = _a.styleQueryMode, styleQueryMode = _c === void 0 ? 'screen' : _c;
    var sizeOrder = ['SMALL', 'MEDIUM', 'LARGE'];
    return sizeOrder.map(function (size) {
        var style = styles.find(function (_a) {
            var screenSize = _a.screenSize;
            return screenSize === size;
        });
        if (style) {
            var config = getTextSetConfig({
                uiElement: 'card',
                size: style.textSize
            });
            return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        ", " {\n          ", "\n        }\n      "], ["\n        ", " {\n          ", "\n        }\n      "])), style.screenSize !== 'SMALL'
                ? breakpoint(style.screenSize.toLowerCase(), {
                    mode: styleQueryMode
                })
                : '', getCTAStyle({ size: config === null || config === void 0 ? void 0 : config.cta }));
        }
        return null;
    });
};
export var CTA = styled.span.attrs(function (props) {
    return parseButtonProps(props);
})(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var ctaStyle = _a.ctaStyle, lightHexCode = _a.lightHexCode, responsiveSizes = _a.responsiveSizes, $size = _a.$size, $styleQueryMode = _a.$styleQueryMode;
    return ctaStyle === 'LINK'
        ? css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n          display: inline-block;\n          color: ", ";\n          text-decoration: underline;\n          ", "\n          ", "\n        "], ["\n          display: inline-block;\n          color: ", ";\n          text-decoration: underline;\n          ", "\n          ", "\n        "])), colors.bombasBlue, lightHexCode && "color: ".concat(lightHexCode, ";"), responsiveSizes
            ? generateCTAResponsiveStyles({
                styles: responsiveSizes,
                styleQueryMode: $styleQueryMode || 'screen'
            })
            : getCTAStyle({ size: $size })) : ButtonStyle;
});
export var Button = styled(ButtonComponent).attrs(function (_a) {
    var isGhostButton = _a.isGhostButton, variant = _a.variant;
    var newVariant = variant;
    if (isGhostButton) {
        newVariant = 'secondary';
    }
    return { variant: newVariant, isGhost: isGhostButton };
})(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  cursor: pointer;\n"], ["\n  cursor: pointer;\n"])));
export var CTAs = styled.div(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  margin-bottom: -16px;\n\n  ", ", ", " {\n    margin-right: 16px;\n    margin-bottom: 16px;\n  }\n"], ["\n  margin-bottom: -16px;\n\n  ", ", ", " {\n    margin-right: 16px;\n    margin-bottom: 16px;\n  }\n"])), CTA, Button);
/**
 * Represents a styled component for a group of CTA buttons.
 * @param buttonOverflow - The overflow behavior of the buttons.
 */
export var CTAGroup = styled.div(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  ", "\n  display: flex;\n  gap: var(--CTAGroup-gap);\n  margin-top: 16px;\n  & > * {\n    flex-shrink: 0;\n  }\n  ", "\n"], ["\n  ", "\n  display: flex;\n  gap: var(--CTAGroup-gap);\n  margin-top: 16px;\n  & > * {\n    flex-shrink: 0;\n  }\n  ", "\n"])), function (_a) {
    var cardType = _a.cardType;
    return "--CTAGroup-gap: ".concat(cardType ? (cardType === 'CONTENT_CARD_MEGA' ? '16px' : '8px') : '16px', ";");
}, function (_a) {
    var buttonOverflow = _a.buttonOverflow;
    switch (buttonOverflow) {
        case 'CAROUSEL':
            return "\n          overflow-x: auto;\n          width: 100vw;\n          align-self: start;\n          /* Hide scrollbar for Chrome, Safari, and Opera */\n          &::-webkit-scrollbar {\n            display: none;\n          }\n          /* Hide scrollbar for Firefox */\n          scrollbar-width: none;\n          /* Hide scrollbar for Edge */\n          -ms-overflow-style: none;\n        ";
        default:
            return "\n          flex-wrap: wrap;\n        ";
    }
});
var getHeaderStyle = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 2 : _b, headerStyle = _a.headerStyle, _c = _a.styleQueryMode, styleQueryMode = _c === void 0 ? 'screen' : _c;
    var font = headerStyle === 'SERIF' ? fontAccent : fontTitle;
    return "\n      ".concat(font(getHeaderSize({ size: size, screenSize: 'SMALL' })), "\n\n      ").concat(breakpoint('medium', { mode: styleQueryMode }), " {\n        ").concat(font(getHeaderSize({ size: size, screenSize: 'MEDIUM' })), "\n      }\n\n      ").concat(breakpoint('large', { mode: styleQueryMode }), " {\n        ").concat(font(getHeaderSize({ size: size, screenSize: 'LARGE' })), "\n      }\n");
};
var getFontFamily = function (fontType) {
    return fontType === 'SERIF' ? 'var(--accent-font)' : 'var(--bombas-font)';
};
var generateResponsiveStyles = function (sizeSettings, letterSpacingSettings, lineHeightSettings, spaceAfter, styleQueryMode) { return css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n  font-size: ", ";\n  letter-spacing: ", ";\n  line-height: ", ";\n  &:not(:last-child) {\n    margin-bottom: ", ";\n  }\n  ", " {\n    font-size: ", ";\n    letter-spacing: ", ";\n    line-height: ", ";\n    &:not(:last-child) {\n      margin-bottom: ", ";\n    }\n  }\n  ", " {\n    font-size: ", ";\n    letter-spacing: ", ";\n    line-height: ", ";\n    &:not(:last-child) {\n      margin-bottom: ", ";\n    }\n  }\n"], ["\n  font-size: ", ";\n  letter-spacing: ", ";\n  line-height: ", ";\n  &:not(:last-child) {\n    margin-bottom: ", ";\n  }\n  ", " {\n    font-size: ", ";\n    letter-spacing: ", ";\n    line-height: ", ";\n    &:not(:last-child) {\n      margin-bottom: ", ";\n    }\n  }\n  ", " {\n    font-size: ", ";\n    letter-spacing: ", ";\n    line-height: ", ";\n    &:not(:last-child) {\n      margin-bottom: ", ";\n    }\n  }\n"])), sizeSettings.mobile, letterSpacingSettings.mobile, lineHeightSettings.mobile, spaceAfter.mobile, breakpoint('medium', { mode: styleQueryMode || 'screen' }), sizeSettings.tablet, letterSpacingSettings.tablet, lineHeightSettings.tablet, spaceAfter.tablet, breakpoint('large', { mode: styleQueryMode || 'screen' }), sizeSettings.desktop, letterSpacingSettings.desktop, lineHeightSettings.desktop, spaceAfter.desktop); };
function setTypeSettings(_a) {
    var size = _a.size, font = _a.font, letterSpacing = _a.letterSpacing, lineHeight = _a.lineHeight, fontWeight = _a.fontWeight, spaceAfter = _a.spaceAfter, _b = _a.styleQueryMode, styleQueryMode = _b === void 0 ? 'screen' : _b;
    var fontFamily = getFontFamily(font);
    return css(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n    font-family: ", ";\n    font-weight: ", ";\n    ", "\n  "], ["\n    font-family: ", ";\n    font-weight: ", ";\n    ", "\n  "])), fontFamily, font === 'SERIF' ? '400' : fontWeight, generateResponsiveStyles(size, letterSpacing, lineHeight, spaceAfter, styleQueryMode));
}
export var Eyebrow = styled.div(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n  color: ", ";\n  text-transform: uppercase;\n  ", "\n  ", "\n\n  // TODO: Temporary conditional, delete getEyebrowStyle after full release of univeral text styles to all content system components\n  ", "\n"], ["\n  color: ", ";\n  text-transform: uppercase;\n  ", "\n  ", "\n\n  // TODO: Temporary conditional, delete getEyebrowStyle after full release of univeral text styles to all content system components\n  ", "\n"])), colors.black, applyTextWrap(), function (_a) {
    var lightHexCode = _a.lightHexCode;
    return lightHexCode && "color: ".concat(lightHexCode, ";");
}, function (_a) {
    var _b = _a.$size, $size = _b === void 0 ? 2 : _b, styleSettings = _a.styleSettings, $styleQueryMode = _a.$styleQueryMode;
    if (styleSettings) {
        return setTypeSettings({
            size: styleSettings.eyebrowSize,
            font: 'SANS_SERIF',
            letterSpacing: styleSettings.eyebrowLetterSpacing,
            lineHeight: styleSettings.eyebrowLineHeight,
            spaceAfter: styleSettings.eyebrowSpaceAfter,
            fontWeight: '700',
            styleQueryMode: $styleQueryMode || 'screen'
        });
    }
    return getEyebrowStyle({ size: $size });
});
export var Header = styled.h2(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n  color: ", ";\n  ", "\n\n  ", "\n  ", "\n// TODO: Temporary conditional, delete getHeaderStyle after full release of univeral text styles to all content system components\n  ", "\n"], ["\n  color: ", ";\n  ", "\n\n  ", "\n  ", "\n// TODO: Temporary conditional, delete getHeaderStyle after full release of univeral text styles to all content system components\n  ", "\n"])), colors.black, applyTextWrap(), function (_a) {
    var lightHexCode = _a.lightHexCode;
    return lightHexCode && "color: ".concat(lightHexCode, ";");
}, function (_a) {
    var headerMaxLineLength = _a.headerMaxLineLength;
    return !!headerMaxLineLength && css(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n      max-width: ", "ch;\n    "], ["\n      max-width: ", "ch;\n    "])), headerMaxLineLength);
}, function (_a) {
    var _b = _a.$size, $size = _b === void 0 ? 5 : _b, headerStyle = _a.headerStyle, styleSettings = _a.styleSettings, $styleQueryMode = _a.$styleQueryMode;
    if (styleSettings) {
        return setTypeSettings({
            size: styleSettings.headerSize,
            font: styleSettings.headerFont,
            letterSpacing: styleSettings.headerLetterSpacing,
            lineHeight: styleSettings.headerLineHeight,
            spaceAfter: styleSettings.headerSpaceAfter,
            fontWeight: '700',
            styleQueryMode: $styleQueryMode || 'screen'
        });
    }
    return getHeaderStyle({
        size: $size,
        headerStyle: headerStyle,
        styleQueryMode: $styleQueryMode || 'screen'
    });
});
var getBodyStyle = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 2 : _b;
    return fontBody(getBodySize({ size: size }));
};
export var Body = styled.div(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n  color: ", ";\n  ", "\n\n  a {\n    color: inherit;\n    text-decoration: underline;\n    text-underline-position: under;\n  }\n  // TODO: Temporary conditional, delete getBodyStyle after full release of univeral text styles to all content system components\n  ", "\n"], ["\n  color: ", ";\n  ", "\n\n  a {\n    color: inherit;\n    text-decoration: underline;\n    text-underline-position: under;\n  }\n  // TODO: Temporary conditional, delete getBodyStyle after full release of univeral text styles to all content system components\n  ", "\n"])), colors.black, function (_a) {
    var lightHexCode = _a.lightHexCode;
    return lightHexCode && "color: ".concat(lightHexCode, ";");
}, function (_a) {
    var _b = _a.$size, $size = _b === void 0 ? 2 : _b, styleSettings = _a.styleSettings, $styleQueryMode = _a.$styleQueryMode;
    if (styleSettings) {
        return setTypeSettings({
            size: styleSettings.bodySize,
            font: styleSettings.bodyFont,
            letterSpacing: styleSettings.bodyLetterSpacing,
            lineHeight: styleSettings.bodyLineHeight,
            spaceAfter: styleSettings.bodySpaceAfter,
            fontWeight: '400',
            styleQueryMode: $styleQueryMode || 'screen'
        });
    }
    return getBodyStyle({ size: $size });
});
export var setVerticalSectionPadding = function (_a) {
    var spacingMode = _a.spacingMode, _b = _a.removeVerticalPadding, removeVerticalPadding = _b === void 0 ? 'NONE' : _b, _c = _a.$styleQueryMode, $styleQueryMode = _c === void 0 ? 'screen' : _c;
    var getPaddingValue = function (area, screenSize) {
        return getTextSectionPadding({
            spacingMode: spacingMode,
            screenSize: screenSize,
            verticalSpacingFor: area
        });
    };
    var removeTopPadding = removeVerticalPadding === 'TOP';
    var removeBottomPadding = removeVerticalPadding === 'BOTTOM';
    var removeTopAndBottomPadding = removeVerticalPadding === 'BOTH';
    return "\n      padding-top: ".concat(removeTopAndBottomPadding || removeTopPadding
        ? 0
        : getPaddingValue('TOP', 'SMALL'), "px;\n      padding-bottom: ").concat(removeTopAndBottomPadding || removeBottomPadding
        ? 0
        : getPaddingValue('BOTTOM', 'SMALL'), "px;\n\n      ").concat(breakpoint('medium', { mode: $styleQueryMode }), " {\n        padding-top: ").concat(removeTopAndBottomPadding || removeTopPadding
        ? 0
        : getPaddingValue('TOP', 'MEDIUM'), "px;\n        padding-bottom: ").concat(removeTopAndBottomPadding || removeBottomPadding
        ? 0
        : getPaddingValue('BOTTOM', 'MEDIUM'), "px;\n      }\n\n      ").concat(breakpoint('large', { mode: $styleQueryMode }), " {\n        padding-top: ").concat(removeTopAndBottomPadding || removeTopPadding
        ? 0
        : getPaddingValue('TOP', 'LARGE'), "px;\n        padding-bottom: ").concat(removeTopAndBottomPadding || removeBottomPadding
        ? 0
        : getPaddingValue('BOTTOM', 'LARGE'), "px;\n      }\n    ");
};
export var setHorizontalTextAlignment = function (_a) {
    var alignment = _a.alignment;
    if (alignment === 'TEXT_LEFT_PAGE_CENTER') {
        return 'justify-content: center; align-items: center;';
    }
    if (alignment === 'TEXT_LEFT_PAGE_LEFT') {
        return 'justify-content: flex-start; align-items: center;';
    }
    if (alignment === 'TEXT_CENTER') {
        return 'justify-content: center; text-align: center;';
    }
};
/**
 * When a text color needs to be chosen automatically, this function instructs what color theme should be rendered
 *
 * @default - returns a dark color
 */
export var getCompliantColorTheme = function (_a) {
    var _b = _a.backgroundColor, backgroundColor = _b === void 0 ? colors.black : _b;
    var preferredColor = readableColor(backgroundColor, colors.black, colors.white);
    return preferredColor !== colors.black ? 'LIGHT' : 'DARK';
};
var getPaddingForTextSize = function (textSize) {
    switch (textSize) {
        case 'MEGA':
            return '32px 32px 36px 32px';
        case 'REGULAR':
            return '24px 24px 28px 24px';
        case 'MINI':
            return '12px 12px 16px 12px';
        default:
            return '24px 24px 28px 24px';
    }
};
/**
 * Generates responsive styles padding for card
 *
 */
export var generateResponsivePadding = function (_a) {
    var _b = _a.responsiveSizes, responsiveSizes = _b === void 0 ? [] : _b, reponsiveInternalSpace = _a.reponsiveInternalSpace, isTransparent = _a.isTransparent, verticalTextPosition = _a.verticalTextPosition, hasImage = _a.hasImage, _c = _a.$styleQueryMode, $styleQueryMode = _c === void 0 ? 'screen' : _c;
    var sizeOrder = ['SMALL', 'MEDIUM', 'LARGE'];
    var sizeToScreenMap = {
        SMALL: 'mobile',
        MEDIUM: 'tablet',
        LARGE: 'desktop'
    };
    return sizeOrder.map(function (size) {
        var style = responsiveSizes.find(function (_a) {
            var screenSize = _a.screenSize;
            return screenSize === size;
        });
        var internalSpace = reponsiveInternalSpace && reponsiveInternalSpace[sizeToScreenMap[size]];
        if (internalSpace) {
            if (internalSpace.includes(',')) {
                var parsedInternalSpace = internalSpace === null || internalSpace === void 0 ? void 0 : internalSpace.split(',');
                return "\n          ".concat(size !== 'SMALL'
                    ? breakpoint(size.toLowerCase(), {
                        mode: $styleQueryMode
                    })
                    : '', " {\n            padding: ").concat(parsedInternalSpace.join('px '), "px;\n          }\n        ");
            }
            return "\n        ".concat(size !== 'SMALL'
                ? breakpoint(size.toLowerCase(), { mode: $styleQueryMode })
                : '', " {\n          padding: ").concat(internalSpace, "px;\n        }\n      ");
        }
        if (verticalTextPosition === 'BELOW_IMAGE' && isTransparent) {
            return "\n        padding: 16px 0 0 0;\n      ";
        }
        if (!hasImage && isTransparent) {
            return "\n        padding: 0;\n      ";
        }
        if (style) {
            return css(templateObject_14 || (templateObject_14 = __makeTemplateObject(["\n        ", " {\n          padding: ", ";\n        }\n      "], ["\n        ", " {\n          padding: ", ";\n        }\n      "])), style.screenSize !== 'SMALL'
                ? breakpoint(style.screenSize.toLowerCase(), {
                    mode: $styleQueryMode
                })
                : '', getPaddingForTextSize(style.textSize));
        }
        return null;
    });
};
export default {};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14;
